// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Konvo's feedback ask, as the person sees it in the Konvo DM ("rate my app"). Five stars
// first (when the ask has a rating), then what kind of note (idea, problem, something they like),
// which part of Konvo, and a text box; Send passes it to the team (Team insights → Feedback) and
// Konvo thanks them in the chat. Once sent, the card shows what they said, with "Add another".
// They can also just reply in the chat. The embed carries only the ask id; the card loads the
// rest. Staff see the same card, not sendable, as a preview in the ask panel.

import Channels from '@app/features/channel/state/Channels';
import Activity from '@app/features/konvo_activity/state/Activity';
import styles from '@app/features/konvo_feedback/components/FeedbackCard.module.css';
import FeedbackAsks from '@app/features/konvo_feedback/state/FeedbackAsks';
import {AREA_LABELS, KIND_LABELS, RATING_LABELS} from '@app/features/konvo_feedback/utils/FeedbackLabels';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {KonvoLogo} from '@app/features/ui/components/icons/KonvoLogo';
import {
	BugIcon,
	ChatCircleTextIcon,
	CheckIcon,
	HeartIcon,
	LightbulbFilamentIcon,
	PlusIcon,
	StarIcon,
} from '@app/features/ui/icons/Icons';
import {
	FEEDBACK_AREAS,
	FEEDBACK_TEXT_MAX,
	type FeedbackAnswer,
	type FeedbackArea,
	type FeedbackAskView,
	type FeedbackKind,
} from '@konvo/schema/src/domains/konvo/AppFeedback';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useEffect, useRef, useState} from 'react';

/** Asks already reported as read this session. */
const reported = new Set<string>();

const KIND_ICONS: Record<FeedbackKind, (props: {size: number}) => ReactNode> = {
	idea: ({size}) => <LightbulbFilamentIcon size={size} />,
	problem: ({size}) => <BugIcon size={size} />,
	praise: ({size}) => <HeartIcon size={size} />,
};

/** The parts offered on the card, most asked about first. */
const AREA_ORDER: ReadonlyArray<FeedbackArea> = [
	'chats',
	'mail',
	'tasks',
	'calendar',
	'whatsapp',
	'assistant',
	'home',
	'notes',
	'calls',
	'phone_app',
	'other',
];

export function Stars({
	value,
	onPick,
	size = 30,
	disabled = false,
	flx,
}: {
	value: number | null;
	onPick?: (value: number) => void;
	size?: number;
	disabled?: boolean;
	flx?: string;
}) {
	const {i18n, t} = useLingui();
	const [hover, setHover] = useState<number | null>(null);
	const shown = hover ?? value ?? 0;
	if (!onPick) {
		return (
			<span className={styles.starsStatic} role="img" aria-label={t`${value ?? 0} of 5 stars`}>
				{[1, 2, 3, 4, 5].map((n) => (
					<StarIcon key={n} size={size} className={clsx(styles.star, n <= (value ?? 0) && styles.starFilled)} />
				))}
			</span>
		);
	}
	return (
		<div
			className={styles.stars}
			role="radiogroup"
			aria-label={t`Your rating`}
			onMouseLeave={() => setHover(null)}
			data-flx={flx}
		>
			{[1, 2, 3, 4, 5].map((n) => (
				<button
					key={n}
					type="button"
					role="radio"
					aria-checked={value === n}
					aria-label={`${n} · ${i18n._(RATING_LABELS[n - 1]!)}`}
					className={clsx(styles.starButton, n <= shown && styles.starOn)}
					disabled={disabled}
					onMouseEnter={() => setHover(n)}
					onFocus={() => setHover(n)}
					onBlur={() => setHover(null)}
					onClick={() => onPick(n)}
					data-flx="konvo-feedback.card.star"
				>
					{/* One icon, filled by CSS: swapping icons mid-click would swallow the click. */}
					<StarIcon size={size} />
				</button>
			))}
		</div>
	);
}

interface CardProps {
	/** The ask as loaded, or the staff preview's stand-in. */
	view: FeedbackAskView;
	/** Staff preview: everything can be tried, nothing is sent. */
	preview?: boolean;
	sending?: boolean;
	onSend?: (input: {
		rating: number | null;
		kind: FeedbackKind | null;
		area: FeedbackArea | null;
		text: string;
	}) => Promise<boolean>;
}

/** The card itself, without loading (FeedbackCard loads it; the staff preview passes a made-up view). */
export const FeedbackCardBody = observer(function FeedbackCardBody({
	view,
	preview = false,
	sending,
	onSend,
}: CardProps) {
	const {i18n, t} = useLingui();
	const answered = view.answers.length > 0;
	const rated = view.answers.find((answer) => answer.rating != null)?.rating ?? null;
	const [writing, setWriting] = useState(false);
	const [rating, setRating] = useState<number | null>(null);
	const [kind, setKind] = useState<FeedbackKind>('idea');
	const [area, setArea] = useState<FeedbackArea | null>(view.area);
	const [text, setText] = useState('');
	const textRef = useRef<HTMLTextAreaElement>(null);
	const showForm = view.open && (writing || (!answered && (!view.rating || rating != null)));

	useEffect(() => {
		if (showForm && rating != null) textRef.current?.focus({preventScroll: true});
	}, [showForm, rating]);

	const placeholder =
		kind === 'problem'
			? t`What happened, and where?`
			: kind === 'praise'
				? t`What works well for you?`
				: t`I wish Konvo could…`;

	const send = async (withText: boolean) => {
		if (!onSend) return;
		const body = withText ? text.trim() : '';
		if (!body && rating == null) return;
		const ok = await onSend({rating, kind: body ? kind : null, area: body ? area : null, text: body});
		if (ok) {
			setWriting(false);
			setText('');
			setRating(null);
		}
	};

	const heading = view.rating && !answered && !writing ? t`How’s Konvo working for you?` : view.question;

	return (
		<article
			className={styles.card}
			data-flx="konvo-feedback.card"
			data-state={showForm ? 'form' : answered ? 'done' : 'ask'}
		>
			<header className={styles.head}>
				<span className={styles.mark} aria-hidden="true">
					<KonvoLogo size={16} />
				</span>
				<div className={styles.headText}>
					<div className={styles.kicker}>{t`Feedback for the Konvo team`}</div>
					<div className={styles.title}>{answered && !writing ? t`Thanks, that’s with the team` : heading}</div>
				</div>
			</header>

			{view.rating && !answered && view.open && (
				<div className={styles.rateRow}>
					<Stars value={rating} onPick={setRating} disabled={sending} flx="konvo-feedback.card.stars" />
					<div className={styles.rateEnds} aria-hidden="true">
						<span>{rating != null ? i18n._(RATING_LABELS[rating - 1]!) : i18n._(RATING_LABELS[0]!)}</span>
						{rating == null && <span>{i18n._(RATING_LABELS[4]!)}</span>}
					</div>
				</div>
			)}

			{showForm && (
				<div className={styles.form} data-flx="konvo-feedback.card.form">
					{(rating != null || writing) && <div className={styles.question}>{view.question}</div>}
					<div className={styles.kinds} role="radiogroup" aria-label={t`Kind of note`}>
						{(['idea', 'problem', 'praise'] as const).map((value) => (
							<button
								key={value}
								type="button"
								role="radio"
								aria-checked={kind === value}
								className={clsx(styles.kind, kind === value && styles.kindOn)}
								onClick={() => setKind(value)}
								data-flx={`konvo-feedback.card.kind.${value}`}
							>
								{KIND_ICONS[value]({size: 15})}
								{i18n._(KIND_LABELS[value])}
							</button>
						))}
					</div>
					<textarea
						ref={textRef}
						className={styles.text}
						value={text}
						onChange={(event) => setText(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
								event.preventDefault();
								void send(true);
							}
						}}
						placeholder={placeholder}
						maxLength={FEEDBACK_TEXT_MAX}
						rows={3}
						aria-label={view.question}
						data-flx="konvo-feedback.card.text"
					/>
					<div className={styles.areaRow}>
						<span className={styles.areaLabel}>{t`About`}</span>
						<div className={styles.areas} role="radiogroup" aria-label={t`Part of Konvo`}>
							{AREA_ORDER.filter((value) => FEEDBACK_AREAS.includes(value)).map((value) => (
								<button
									key={value}
									type="button"
									role="radio"
									aria-checked={area === value}
									className={clsx(styles.area, area === value && styles.areaOn)}
									onClick={() => setArea(area === value ? null : value)}
									data-flx="konvo-feedback.card.area"
								>
									{i18n._(AREA_LABELS[value])}
								</button>
							))}
						</div>
					</div>
					<div className={styles.actions}>
						{rating != null && !text.trim() && (
							<button
								type="button"
								className={styles.quiet}
								disabled={preview || sending}
								onClick={() => void send(false)}
								data-flx="konvo-feedback.card.rating-only"
							>
								{t`Just send the rating`}
							</button>
						)}
						{writing && (
							<button
								type="button"
								className={styles.quiet}
								onClick={() => {
									setWriting(false);
									setText('');
								}}
							>
								{t`Cancel`}
							</button>
						)}
						<span className={styles.spacer} />
						<button
							type="button"
							className={styles.send}
							disabled={preview || sending || !text.trim()}
							onClick={() => void send(true)}
							data-flx="konvo-feedback.card.send"
						>
							{t`Send to the team`}
						</button>
					</div>
				</div>
			)}

			{answered && !writing && (
				<div className={styles.sent} data-flx="konvo-feedback.card.sent">
					{rated != null && (
						<div className={styles.sentRating}>
							<Stars value={rated} size={16} />
							<span>{i18n._(RATING_LABELS[rated - 1]!)}</span>
						</div>
					)}
					{view.answers
						.filter((answer) => answer.text.trim())
						.map((answer) => (
							<SentNote key={answer.answer_id} answer={answer} />
						))}
					{view.open && (
						<button
							type="button"
							className={styles.addAnother}
							onClick={() => setWriting(true)}
							data-flx="konvo-feedback.card.add"
						>
							<PlusIcon size={14} />
							{t`Add another`}
						</button>
					)}
				</div>
			)}

			{!view.open && !answered && <div className={styles.closed}>{t`This ask has closed. Thanks anyway.`}</div>}
		</article>
	);
});

function SentNote({answer}: {answer: FeedbackAnswer}) {
	const {i18n} = useLingui();
	return (
		<div className={styles.note}>
			<span className={styles.noteIcon}>
				{answer.kind ? KIND_ICONS[answer.kind]({size: 14}) : <ChatCircleTextIcon size={14} />}
			</span>
			<span className={styles.noteText}>
				{answer.text}
				{answer.area && <span className={styles.noteArea}> · {i18n._(AREA_LABELS[answer.area])}</span>}
			</span>
			<CheckIcon size={14} className={styles.noteCheck} />
		</div>
	);
}

/** The card in the Konvo DM: loads the ask by id; apps or servers that cannot fall back to plain text. */
export const FeedbackCard = observer(function FeedbackCard({
	askId,
	title,
	fallback,
	channelId,
}: {
	askId: string;
	title: string | null;
	fallback: string | null;
	/** The Konvo DM: a new message there may be an answer written in the chat, so the card reloads. */
	channelId: string;
}) {
	const {t} = useLingui();
	const root = useRef<HTMLDivElement>(null);
	const state = FeedbackAsks.get(askId);
	const lastMessageId = Channels.getChannel(channelId)?.lastMessageId ?? null;
	const seenLast = useRef(lastMessageId);

	useEffect(() => {
		FeedbackAsks.load(askId);
	}, [askId]);

	useEffect(() => {
		if (lastMessageId === seenLast.current) return;
		seenLast.current = lastMessageId;
		if (FeedbackAsks.get(askId)?.status === 'ready') FeedbackAsks.load(askId, true);
	}, [askId, lastMessageId]);

	useEffect(() => {
		const node = root.current;
		if (!node || reported.has(askId)) return;
		const observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting) && !reported.has(askId)) {
				reported.add(askId);
				Activity.track('feedback.read', {ask_id: askId});
				observer.disconnect();
			}
		});
		observer.observe(node);
		return () => observer.disconnect();
	}, [askId]);

	const onSend: CardProps['onSend'] = async (input) => {
		try {
			await FeedbackAsks.answer(askId, input);
			return true;
		} catch {
			ToastCommands.createToast({type: 'error', children: t`Could not send that. Try again.`});
			return false;
		}
	};

	return (
		<div ref={root} className={styles.wrap} data-flx="konvo-feedback">
			{state?.status === 'ready' ? (
				<FeedbackCardBody view={state.view} sending={FeedbackAsks.isSending(askId)} onSend={onSend} />
			) : (
				<article className={styles.card} data-flx="konvo-feedback.card" data-state={state?.status ?? 'loading'}>
					<header className={styles.head}>
						<span className={styles.mark} aria-hidden="true">
							<KonvoLogo size={16} />
						</span>
						<div className={styles.headText}>
							<div className={styles.kicker}>{t`Feedback for the Konvo team`}</div>
							<div className={styles.title}>{title ?? t`How’s Konvo working for you?`}</div>
						</div>
					</header>
					{state?.status === 'missing' && fallback && <div className={styles.closed}>{fallback}</div>}
				</article>
			)}
			<div className={styles.smallPrint}>{t`Goes to the Konvo team with your name · you can also just reply here`}</div>
		</div>
	);
});
